import { useState, useCallback, useEffect } from 'react';
import { useApi, ApiConfig } from './useApi';
import { Subnet } from './types';

export interface UseSubnetsOptions {
  autoFetch?: boolean;
}

export function useSubnets(
  config: ApiConfig = {},
  options: UseSubnetsOptions = {},
) {
  const { autoFetch = true } = options;
  const api = useApi(config);
  const [subnets, setSubnets] = useState<Subnet[]>([]);

  const fetchSubnets = useCallback(async () => {
    try {
      const response = await api.get<Subnet[]>('/subnets/');
      setSubnets(response);
      return response;
    } catch (error) {
      console.error('Failed to fetch subnets:', error);
      throw error;
    }
  }, [api]);

  const getSubnet = useCallback(
    async (id: number) => {
      return api.get<Subnet>(`/subnets/${id}/`);
    },
    [api],
  );

  const createSubnet = useCallback(
    async (data: Omit<Subnet, 'id' | 'subnet_mask' | 'total_available_addresses'>) => {
      const response = await api.post<Subnet>('/subnets/', data);
      await fetchSubnets();
      return response;
    },
    [api, fetchSubnets],
  );

  const updateSubnet = useCallback(
    async (id: number, data: Partial<Omit<Subnet, 'id' | 'subnet_mask' | 'total_available_addresses'>>) => {
      const response = await api.patch<Subnet>(`/subnets/${id}/`, data);
      await fetchSubnets();
      return response;
    },
    [api, fetchSubnets],
  );

  const deleteSubnet = useCallback(
    async (id: number) => {
      await api.delete(`/subnets/${id}/`);
      await fetchSubnets();
    },
    [api, fetchSubnets],
  );

  const startScan = useCallback(
    async (id: number) => {
      const response = await api.post<{ id: number }>(`/scans/${id}`);
      return response;
    },
    [api],
  );

  useEffect(() => {
    if (autoFetch && config.token) {
      fetchSubnets();
    }
  }, [autoFetch, config.token, fetchSubnets]);

  return {
    subnets,
    loading: api.loading,
    error: api.error,
    fetchSubnets,
    getSubnet,
    createSubnet,
    updateSubnet,
    deleteSubnet,
    startScan,
  };
}